import React, { useState } from 'react';
import { api, Variable } from '../api/client';

interface Props {
  questionId: string;
  variables: Variable[];
  onVariableAdded: () => void;
}

const VariableManager: React.FC<Props> = ({ questionId, variables, onVariableAdded }) => {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'numeric' | 'categorical' | 'boolean'>('boolean');
  const [dependent, setDependent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Variable name is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await api.createVariable(questionId, {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        dependent,
      });
      setName('');
      setDescription('');
      setType('boolean');
      setDependent(false);
      setShowForm(false);
      onVariableAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create variable');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (variableId: string, variableName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${variableName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(variableId);
      setError(null);
      await api.deleteVariable(variableId);
      onVariableAdded(); // Reload the variables
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete variable');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const depVariables = variables.filter(v => v.dependent);
  const indepVariables = variables.filter(v => !v.dependent);

  return (
    <div className="variable-manager">
      {error && <div className="error">{error}</div>}
      
      <div className="section">
        <h3>Dependent Variables (Outcomes)</h3>
        {depVariables.length === 0 ? (
          <p className="empty-state">No dependent variables yet. These represent the outcomes you want to understand.</p>
        ) : (
          <ul className="variable-list">
            {depVariables.map(v => (
              <li key={v.id} className="variable-item">
                <div className="variable-info">
                  <div className="variable-header">
                    <strong>{v.name}</strong>
                    <span className="variable-type">{v.type}</span>
                  </div>
                  {v.description && <p>{v.description}</p>}
                </div>
                <button
                  onClick={() => handleDelete(v.id, v.name)}
                  className="btn-delete"
                  disabled={deletingId === v.id}
                  title="Delete variable"
                >
                  {deletingId === v.id ? '...' : '×'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="section">
        <h3>Independent Variables (Predictors)</h3>
        {indepVariables.length === 0 ? (
          <p className="empty-state">No independent variables yet. These represent the factors you think might affect the outcome.</p>
        ) : (
          <ul className="variable-list">
            {indepVariables.map(v => (
              <li key={v.id} className="variable-item">
                <div className="variable-info">
                  <div className="variable-header">
                    <strong>{v.name}</strong>
                    <span className="variable-type">{v.type}</span>
                  </div>
                  {v.description && <p>{v.description}</p>}
                </div>
                <button
                  onClick={() => handleDelete(v.id, v.name)}
                  className="btn-delete"
                  disabled={deletingId === v.id}
                  title="Delete variable"
                >
                  {deletingId === v.id ? '...' : '×'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="btn btn-primary">
          Add Variable
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="variable-form">
          <div className="form-group">
            <label htmlFor="name">Variable Name *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Dog showed symptoms"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Variable Type</label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as any)}
            >
              <option value="boolean">Boolean (yes/no)</option>
              <option value="numeric">Numeric</option>
              <option value="categorical">Categorical</option>
            </select>
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={dependent}
                onChange={(e) => setDependent(e.target.checked)}
              />
              <span>This is a dependent variable (outcome)</span>
            </label>
            <small>Uncheck if this is an independent variable (predictor)</small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setName('');
                setDescription('');
                setError(null);
              }}
              className="btn btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Variable'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default VariableManager;
